import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useApplicationStore } from '../../stores/applicationStore';
import usePlacesAutocomplete, { getDetails } from 'use-places-autocomplete';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotificationStore } from '../../stores/notificationStore';
import { ref, uploadBytes, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { storage } from '../../services/firebase.config';
import { useAuthStore } from '../../stores/authStore';
import { CreateApplicationData } from '../../services/applicationService';
import { ContactMethod } from '../../types';
import { format } from 'date-fns';
import { 
  Upload, 
  X, 
  FileText, 
  Building2, 
  MapPin, 
  Calendar, 
  Mail, 
  Phone, 
  Globe, 
  MessageSquare,
  User,
  Save,
  ArrowLeft
} from 'lucide-react';
import { JobApplication } from '../../types';

const applicationSchema = z.object({
  companyName: z.string().min(2, 'Nome azienda obbligatorio'),
  companyAddress: z.string().min(2, 'Indirizzo obbligatorio'),
  companyCity: z.string().optional(),
  companyPostalCode: z.string().optional(),
  companyCountry: z.string().optional(),
  position: z.string().min(2, 'Posizione obbligatoria'),
  contactMethod: z.string().min(2, 'Metodo di contatto obbligatorio'),
  status: z.string().min(1, 'Stato obbligatorio'),
  appliedDate: z.string().min(1, 'Data obbligatoria'),
  notes: z.string().optional(),
  attachments: z.any().optional(),
});

type ApplicationFormData = z.infer<typeof applicationSchema>;

const contactMethods = [
  'Email',
  'Telefono',
  'Form web',
  'LinkedIn',
  'Lettera',
  'Altro',
];

const statusOptions = [
  'In attesa',
  'Colloquio programmato',
  'Accettato',
  'Rifiutato',
  'Scaduto',
];

interface ApplicationFormProps {
  initialApplication?: JobApplication;
  isEditMode?: boolean;
}

export const ApplicationForm: React.FC<ApplicationFormProps> = ({ 
  initialApplication, 
  isEditMode = false 
}) => {
  const { createApplication, applications, updateApplicationInFirestore, loadApplications } = useApplicationStore();
  const [uploadedFiles, setUploadedFiles] = useState<{ name: string; url: string; type: string; size: number }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFile, setUploadingFile] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();
  const addNotification = useNotificationStore(s => s.addNotification);
  const { user } = useAuthStore();
  const inputRef = useRef<HTMLInputElement>(null);
  const { id } = useParams<{ id: string }>();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm<ApplicationFormData>({
    resolver: zodResolver(applicationSchema),
    defaultValues: {
      appliedDate: new Date().toISOString().slice(0, 10),
      status: 'In attesa',
    },
  });

  // Google Places Autocomplete
  const {
    ready,
    value: placesValue,
    suggestions: { status, data },
    setValue: setPlacesValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'ch' },
    },
    debounce: 300,
  });

  // Effetto per caricare i dati della candidatura esistente
  useEffect(() => {
    const loadApplication = async () => {
      // Se abbiamo una candidatura iniziale (dalla pagina di modifica), usala
      if (initialApplication) {
        console.log('✅ Caricamento dati da initialApplication:', initialApplication.companyDetails.name);
        setValue('companyName', initialApplication.companyDetails.name);
        setValue('companyAddress', initialApplication.companyDetails.address);
        setValue('companyCity', initialApplication.companyDetails.city);
        setValue('companyPostalCode', initialApplication.companyDetails.postalCode);
        setValue('companyCountry', initialApplication.companyDetails.country);
        setValue('position', initialApplication.position);
        setValue('contactMethod', initialApplication.contactMethod);
        setValue('status', initialApplication.status);
        setValue('appliedDate', format(initialApplication.appliedDate.toDate(), 'yyyy-MM-dd'));
        setValue('notes', initialApplication.notes);
        setUploadedFiles(initialApplication.attachments.map(att => ({
          name: att.name,
          url: att.url,
          type: att.type,
          size: att.size || 0
        })));
        setPlacesValue(initialApplication.companyDetails.name);
        return;
      }

      // Altrimenti, usa la logica esistente per il routing con ID
      if (id && user?.uid && !initialApplication) { // Aggiunto controllo !initialApplication
        try {
          // Se le candidature non sono ancora caricate, caricale prima
          if (applications.length === 0) {
            console.log('🔄 Caricamento candidature per trovare la candidatura da modificare...');
            await loadApplications(user.uid);
          }
          
          const application = applications.find(app => app.id === id);
          if (application) {
            console.log('✅ Candidatura trovata per modifica:', application.companyName);
            setValue('companyName', application.companyDetails.name);
            setValue('companyAddress', application.companyDetails.address);
            setValue('companyCity', application.companyDetails.city);
            setValue('companyPostalCode', application.companyDetails.postalCode);
            setValue('companyCountry', application.companyDetails.country);
            setValue('position', application.position);
            setValue('contactMethod', application.contactMethod);
            setValue('status', application.status);
            setValue('appliedDate', format(application.appliedDate.toDate(), 'yyyy-MM-dd'));
            setValue('notes', application.notes);
            setUploadedFiles(application.attachments.map(att => ({
              name: att.name,
              url: att.url,
              type: att.type,
              size: att.size || 0
            })));
            setPlacesValue(application.companyDetails.name);
          } else {
            console.log('❌ Candidatura non trovata con ID:', id);
            addNotification({
              type: 'warning',
              title: 'Candidatura non trovata',
              message: 'La candidatura con questo ID non è stata trovata.'
            });
            navigate('/applications');
          }
        } catch (error) {
          console.error('Errore nel caricamento candidatura:', error);
          addNotification({
            type: 'warning',
            title: 'Errore caricamento',
            message: 'Errore durante il caricamento dei dati della candidatura.'
          });
        }
      }
    };

    loadApplication();
  }, [id, user?.uid, applications, setValue, addNotification, navigate, setPlacesValue, loadApplications, initialApplication]);

  // Gestione selezione suggerimento o TAB
  const handlePlaceSelect = async (placeId: string, description?: string) => {
    try {
      const place = await getDetails({ placeId });
      if (place && typeof place === 'object' && 'address_components' in place) {
        const addressComponents = place.address_components || [];
        const streetNumber = addressComponents.find((c: any) => c.types.includes('street_number'))?.long_name || '';
        const route = addressComponents.find((c: any) => c.types.includes('route'))?.long_name || '';
        const city = addressComponents.find((c: any) => c.types.includes('locality'))?.long_name || '';
        const postalCode = addressComponents.find((c: any) => c.types.includes('postal_code'))?.long_name || '';
        const country = addressComponents.find((c: any) => c.types.includes('country'))?.long_name || '';
        // Inverti il formato: prima via, poi numero
        const fullAddress = route && streetNumber ? `${route} ${streetNumber}`.trim() : `${route}${streetNumber}`.trim();
        setValue('companyName', description ? description.split(',')[0] : placesValue);
        setPlacesValue(description ? description.split(',')[0] : placesValue);
        setValue('companyAddress', fullAddress);
        setValue('companyCity', city);
        setValue('companyPostalCode', postalCode);
        setValue('companyCountry', country);
        // Chiudi il dropdown e rimuovi il focus
        clearSuggestions();
        setShowSuggestions(false);
        if (inputRef.current) {
          inputRef.current.blur();
        }
        // Forza la chiusura del dropdown con timeout multipli
        setTimeout(() => {
          clearSuggestions();
          setShowSuggestions(false);
          if (inputRef.current) {
            inputRef.current.blur();
          }
        }, 50);
        setTimeout(() => {
          clearSuggestions();
          setShowSuggestions(false);
          if (inputRef.current) {
            inputRef.current.blur();
          }
        }, 200);
      }
    } catch (error) {
      console.error('Errore nel recupero dettagli luogo:', error);
    }
  };

  // Gestione TAB
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' && status === 'OK' && data.length > 0) {
      // Se c'è un suggerimento, seleziona il primo
      handlePlaceSelect(data[0].place_id, data[0].description);
      // Previeni il comportamento di default del TAB
      e.preventDefault();
    }
  };

  const validateFile = (file: File): string | null => {
    // Controlla dimensione (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      return 'File troppo grande. Dimensione massima: 10MB';
    }
    
    // Controlla tipo file
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      return 'Tipo di file non supportato. Usa PDF, Word o file di testo';
    }
    
    return null;
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    console.log('📁 File selezionati:', files.length, 'file');
    console.log('👤 User UID:', user?.uid);
    console.log('👤 User completo:', user);
    console.log('🔥 Storage configurato:', !!storage);
    console.log('🔥 Storage app:', storage?.app);

    if (!user?.uid) {
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Utente non autenticato. Effettua il login.'
      });
      return;
    }

    // Verifica che Firebase Storage sia configurato
    if (!storage) {
      addNotification({
        type: 'warning',
        title: 'Errore configurazione',
        message: 'Firebase Storage non è configurato correttamente.'
      });
      return;
    }

    // Test di connessione a Firebase Storage
    try {
      const testRef = ref(storage, `test/${user.uid}/test.txt`);
      console.log('🧪 Test connessione Storage:', testRef.fullPath);
      console.log('🌐 Dominio corrente:', window.location.origin);
      console.log('🔧 Configurazione CORS attiva per dominio personalizzato');
    } catch (error) {
      console.error('❌ Errore test Storage:', error);
      addNotification({
        type: 'warning',
        title: 'Errore Storage',
        message: 'Impossibile connettersi a Firebase Storage.'
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    
    try {
      const validFiles: File[] = [];
      const errors: string[] = [];

      // Valida tutti i file prima dell'upload
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const error = validateFile(file);
        if (error) {
          errors.push(`${file.name}: ${error}`);
        } else {
          validFiles.push(file);
        }
      }

      // Mostra errori di validazione
      if (errors.length > 0) {
        addNotification({
          type: 'warning',
          title: 'File non validi',
          message: errors.join(', ')
        });
        if (validFiles.length === 0) {
          setUploading(false);
          return;
        }
      }

      // Upload sequenziale per evitare problemi di progress tracking
      const newFiles: { name: string; url: string; type: string; size: number }[] = [];
      
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        const fileName = `${Date.now()}_${i}_${file.name}`;
        const storageRef = ref(storage, `attachments/${user.uid}/${fileName}`);
        
        console.log('📤 Iniziando upload per:', file.name);
        console.log('📁 Storage ref:', storageRef.fullPath);
        
        setUploadingFile(file.name);
        
        // Usa uploadBytesResumable per il progress tracking
        const uploadTask = uploadBytesResumable(storageRef, file);
        
        try {
          const result = await new Promise<{ name: string; url: string; type: string; size: number }>((resolve, reject) => {
            uploadTask.on('state_changed',
              (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                console.log(`📊 Progress ${file.name}: ${progress.toFixed(1)}% (${snapshot.bytesTransferred}/${snapshot.totalBytes})`);
                setUploadProgress(progress);
              },
            (error) => {
              console.error('❌ Errore upload file:', file.name, error);
              console.error('❌ Error code:', error.code);
              console.error('❌ Error message:', error.message);
              reject(new Error(`Errore upload ${file.name}: ${error.message}`));
            },
            async () => {
              try {
                console.log('✅ Upload completato per:', file.name);
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                console.log('🔗 Download URL ottenuto per:', file.name);
                resolve({
                  name: file.name,
                  url: downloadURL,
                  type: file.type,
                  size: file.size
                });
              } catch (error) {
                console.error('❌ Errore download URL per:', file.name, error);
                reject(new Error(`Errore download URL per ${file.name}`));
              }
            }
          );
        });
        
        // Aggiungi il file caricato alla lista
        newFiles.push(result);
        console.log('✅ File aggiunto alla lista:', file.name);
      } catch (error) {
        console.error('❌ Errore durante upload di:', file.name, error);
        throw error;
      }
    }
      
      setUploadedFiles(prev => [...prev, ...newFiles]);
      setValue('attachments', newFiles);
      
      if (newFiles.length > 0) {
        addNotification({
          type: 'info',
          title: 'Upload completato',
          message: `${newFiles.length} file caricati con successo.`
        });
      }
    } catch (error) {
      console.error('Errore upload file:', error);
      addNotification({
        type: 'warning',
        title: 'Errore upload',
        message: error instanceof Error ? error.message : 'Errore durante il caricamento dei file.'
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadingFile(null);
      // Reset input file
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const onSubmit = async (data: ApplicationFormData) => {
    console.log('🔄 onSubmit chiamato con dati:', data);
    console.log('🔄 isEditMode:', isEditMode, 'id:', id);
    
    if (!user?.uid) {
      console.log('❌ Utente non autenticato');
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Utente non autenticato.'
      });
      return;
    }

    setSubmitting(true);
    try {
      console.log('🔄 Preparazione dati candidatura...');
      const applicationData: CreateApplicationData = {
        companyName: data.companyName,
        position: data.position,
        contactMethod: data.contactMethod as ContactMethod,
        appliedDate: new Date(data.appliedDate),
        status: data.status as any,
        notes: data.notes || '',
        companyDetails: {
          name: data.companyName,
          address: data.companyAddress || '',
          city: data.companyCity || '',
          postalCode: data.companyPostalCode || '',
          country: data.companyCountry || '',
        },
        attachments: uploadedFiles,
      };

      console.log('🔄 Dati preparati:', applicationData);

      if (id) {
        console.log('🔄 Aggiornamento candidatura esistente...');
        await updateApplicationInFirestore(user.uid, id, applicationData);
        console.log('✅ Candidatura aggiornata con successo');
        addNotification({
          type: 'info',
          title: 'Candidatura aggiornata',
          message: 'La candidatura è stata aggiornata con successo!',
        });
      } else {
        console.log('🔄 Creazione nuova candidatura...');
        await createApplication(user.uid, applicationData);
        console.log('✅ Candidatura creata con successo');
        addNotification({
          type: 'info',
          title: 'Candidatura salvata',
          message: 'La candidatura è stata aggiunta con successo!',
        });
      }
      
      console.log('🔄 Reset form e redirect...');
      setUploadedFiles([]);
      reset();
      
      navigate('/applications');
    } catch (error) {
      console.error('❌ Errore nel salvataggio candidatura:', error);
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Errore durante il salvataggio della candidatura.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {isEditMode || id ? 'Modifica Candidatura' : 'Nuova Candidatura'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {isEditMode || id ? 'Modifica i dettagli della tua candidatura esistente.' : 'Inserisci i dettagli della tua candidatura.'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Nome Azienda */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Nome Azienda *
          </label>
          <input
            type="text"
            ref={inputRef}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="Inizia a digitare il nome dell'azienda..."
            value={placesValue}
            onChange={e => {
              setPlacesValue(e.target.value);
              setValue('companyName', e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => {
              setTimeout(() => setShowSuggestions(false), 200);
            }}
            onKeyDown={handleKeyDown}
            disabled={!ready}
            autoComplete="off"
          />
          {errors.companyName && (
            <p className="mt-1 text-sm text-red-600">{errors.companyName.message}</p>
          )}
          {status === 'OK' && showSuggestions && data.length > 0 && (
            <ul className="mt-2 border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 shadow-lg max-h-60 overflow-auto z-50">
              {data.map((suggestion) => (
                <li
                  key={suggestion.place_id}
                  className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-sm"
                  onClick={() => handlePlaceSelect(suggestion.place_id, suggestion.description)}
                >
                  {suggestion.description}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Indirizzo Azienda */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Indirizzo Azienda *
          </label>
          <input
            type="text"
            {...register('companyAddress')}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="Indirizzo completo"
          />
          {errors.companyAddress && (
            <p className="mt-1 text-sm text-red-600">{errors.companyAddress.message}</p>
          )}
        </div>

        {/* Città e CAP */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Città
            </label>
            <input
              type="text"
              {...register('companyCity')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Città"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              CAP
            </label>
            <input
              type="text"
              {...register('companyPostalCode')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="CAP"
            />
          </div>
        </div>

        {/* Posizione */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Posizione Lavorativa *
          </label>
          <input
            type="text"
            {...register('position')}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="Es. Frontend Developer, Product Manager..."
          />
          {errors.position && (
            <p className="mt-1 text-sm text-red-600">{errors.position.message}</p>
          )}
        </div>

        {/* Metodo di Contatto e Stato */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Metodo di Contatto *
            </label>
            <select
              {...register('contactMethod')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Seleziona metodo</option>
              {contactMethods.map((method) => (
                <option key={method} value={method}>
                  {method}
                </option>
              ))}
            </select>
            {errors.contactMethod && (
              <p className="mt-1 text-sm text-red-600">{errors.contactMethod.message}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Stato *
            </label>
            <select
              {...register('status')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            {errors.status && (
              <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
            )}
          </div>
        </div>

        {/* Data Candidatura */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Data Candidatura *
          </label>
          <input
            type="date"
            {...register('appliedDate')}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          />
          {errors.appliedDate && (
            <p className="mt-1 text-sm text-red-600">{errors.appliedDate.message}</p>
          )}
        </div>

        {/* Note */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Note
          </label>
          <textarea
            {...register('notes')}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="Note aggiuntive sulla candidatura..."
          />
        </div>

        {/* Upload File */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Allegati
          </label>
          <div className="space-y-3">
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              disabled={uploading}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
              accept=".pdf,.doc,.docx,.txt"
            />
            
            {/* Barra di caricamento */}
            {uploading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm text-blue-600">
                  <span>Caricamento in corso...</span>
                  <span className="animate-pulse">⏳</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                {uploadingFile && (
                  <p className="text-sm text-blue-600">Caricamento file: {uploadingFile} ({uploadProgress.toFixed(0)}%)</p>
                )}
              </div>
            )}
            
            {/* Lista file caricati */}
            {uploadedFiles.length > 0 && (
              <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <p className="text-sm font-medium text-green-700 dark:text-green-300 mb-2 flex items-center">
                  <span className="mr-2">✓</span>
                  File caricati ({uploadedFiles.length})
                </p>
                <ul className="space-y-1">
                  {uploadedFiles.map((file, index) => (
                    <li key={index} className="text-sm text-green-600 dark:text-green-400 flex items-center justify-between">
                      <span className="flex items-center">
                        <span className="mr-2">📎</span>
                        {file.name}
                      </span>
                      <span className="text-xs text-green-500">
                        ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Pulsanti */}
        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={() => navigate('/applications')}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={submitting}
            onClick={() => console.log('🔄 Bottone submit cliccato')}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {submitting ? 'Salvataggio...' : 'Salva Candidatura'}
          </button>
        </div>
      </form>
    </div>
  );
}; 